/**
 * Which build this is.
 *
 * Stamped in when Vite runs rather than fetched from anywhere: the app is
 * static files behind nginx, so at runtime there is nobody to ask. Of the
 * three parts, the version is the package's and rarely moves, the commit says
 * what the code was, and the time is the only one guaranteed to differ between
 * any two deploys — which makes it the part that actually answers "am I on the
 * latest?".
 *
 * `__BW_BUILD__` is a `define` in `vite.config.ts`. Nothing replaces it under
 * vitest, where the identifier is undeclared rather than undefined, so it is
 * read through `typeof` and falls back to an empty stamp.
 */

export interface BuildStamp {
  /** The app package's version. */
  readonly version: string;
  /** The commit it was built from, short form. Empty when nothing said. */
  readonly commit: string;
  /** When Vite ran, ISO 8601 in UTC. Empty when nothing said. */
  readonly builtAt: string;
}

declare const __BW_BUILD__: BuildStamp | undefined;

const NOTHING: BuildStamp = { version: '', commit: '', builtAt: '' };

/** What this bundle was stamped with; all empty when nothing stamped it. */
export const BUILD: BuildStamp = typeof __BW_BUILD__ === 'undefined' ? NOTHING : __BW_BUILD__;

/**
 * The build in one line, for the footer of the controls dialog.
 *
 * Sortable rather than friendly, on purpose: the questions it has to answer
 * are whether two people are on the same build and which of them is older, and
 * "6 September" answers neither as quickly as the stamp does.
 */
export function versionLabel(build: BuildStamp = BUILD): string {
  const parts: string[] = [];
  if (build.version !== '') parts.push(`v${build.version}`);
  if (build.commit !== '') parts.push(build.commit);
  const built = builtLabel(build.builtAt);
  if (built !== '') parts.push(built);
  return parts.length === 0 ? 'Development build' : parts.join(' · ');
}

/** `2026-09-06T14:32:10.482Z` as `built 2026-09-06 14:32 UTC`. */
function builtLabel(iso: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(iso);
  return match === null ? '' : `built ${match[1] as string} ${match[2] as string} UTC`;
}
